import { getPing } from '@common/api/ping';
import { Button, StatusBadge, type StatusTone, Surface, Text } from '@hotam/component';
import { useCallback, useEffect, useState } from 'react';

type ApiState = 'checking' | 'healthy' | 'unavailable';

const stateLabel: Record<ApiState, string> = {
  checking: '연결 확인 중',
  healthy: 'API 연결 정상',
  unavailable: 'API 연결 실패',
};

const stateTone: Record<ApiState, StatusTone> = {
  checking: 'info',
  healthy: 'success',
  unavailable: 'danger',
};

export default function App() {
  const [apiState, setApiState] = useState<ApiState>('checking');

  const checkApi = useCallback(async (signal?: AbortSignal) => {
    setApiState('checking');
    try {
      await getPing(signal);
      setApiState('healthy');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setApiState('unavailable');
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    checkApi(controller.signal);
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'reqRenderComplete' }));
    return () => controller.abort();
  }, [checkApi]);

  return (
    <main className='shell'>
      <section className='hero'>
        <Text
          as='p'
          variant='caption'
          className='eyebrow'
        >
          HOTAM · INITIAL SERVICE
        </Text>
        <Text
          as='h1'
          variant='display'
          className='hero-title'
        >
          시작을 담는 <span>가장 단단한 그릇.</span>
        </Text>
        <Text
          as='p'
          variant='body-lg'
          className='description'
        >
          Hotam의 웹, 앱, API가 연결되었습니다.
        </Text>

        <Surface
          as='section'
          elevated
          className='status-card'
          aria-label='API 연결 상태'
        >
          <div className='status-card__content'>
            <Text
              as='h2'
              variant='title-sm'
            >
              서비스 상태
            </Text>
            <StatusBadge tone={stateTone[apiState]}>{stateLabel[apiState]}</StatusBadge>
          </div>
          <Button
            type='button'
            variant='primary'
            size='md'
            onClick={() => {
              checkApi();
            }}
            disabled={apiState === 'checking'}
          >
            다시 확인
          </Button>
        </Surface>
      </section>
      <Text
        as='footer'
        variant='micro'
      >
        hotam.kr
      </Text>
    </main>
  );
}
